import { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, Filter, Clock, Users, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import TaskCard from '../TaskCard';
import AddTaskDialog from '../AddTaskDialog';
import { taskService } from '@/services/task.service';
import { authService } from '@/services/auth.service';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { permissions } from '@/lib/mockAuth';
import { canAddResource, isTrialExpired } from '@/utils/trialUtils';
import { BlockedActionDialog } from '@/components/BlockedActionDialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export const TaskModule = ({ weddingId, wedding }) => {
  const { activeOrg, userRole } = useAuth();
  const canManage = permissions.canManageTasks(userRole);
  const isReadOnly = permissions.isReadOnly(userRole);
  const [tasks, setTasks] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [blockedDialogOpen, setBlockedDialogOpen] = useState(false);

  // Check trial limits
  const taskLimitCheck = canAddResource(wedding, 'tasks', tasks.length);
  const isBlocked = !taskLimitCheck.canAdd || isTrialExpired(wedding);

  useEffect(() => {
    loadTasks();
  }, [weddingId, activeOrg]);

  const loadTasks = async () => {
    if (!weddingId || !activeOrg?.id) return;
    
    try {
      setLoading(true);
      
      // Load tasks and team members in parallel
      const [tasksData, membersData] = await Promise.all([
        taskService.getTasks(weddingId, activeOrg.id),
        authService.getTeamMembers(activeOrg.id)
      ]);
      
      setTeamMembers(membersData);
      
      // Map team member IDs to names
      const tasksWithNames = tasksData.map(task => {
        if (task.assignedTo) {
          const member = membersData.find(m => m.id === task.assignedTo);
          return {
            ...task,
            assignedToName: member?.name || null
          };
        }
        return task;
      });
      
      setTasks(tasksWithNames);
    } catch (error) {
      console.error('Failed to load tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  // Get unique assignees for filter (use names if available, otherwise IDs)
  const assignees = [...new Set(tasks.map(t => t.assignedTo))].filter(Boolean);
  
  // Create a map of assignee IDs to names for the filter dropdown
  const assigneeMap = tasks.reduce((acc, task) => {
    if (task.assignedTo && !acc[task.assignedTo]) {
      acc[task.assignedTo] = task.assignedToName || task.assignedTo;
    }
    return acc;
  }, {});

  // Filter tasks by priority and assignee
  const filteredTasks = tasks.filter(task => {
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesAssignee = assigneeFilter === 'all' || task.assignedTo === assigneeFilter;
    return matchesPriority && matchesAssignee;
  });

  // Group tasks by status
  const columns = {
    todo: filteredTasks.filter(t => t.status === 'todo'),
    'in-progress': filteredTasks.filter(t => t.status === 'in-progress'),
    blocked: filteredTasks.filter(t => t.status === 'blocked'),
    done: filteredTasks.filter(t => t.status === 'done'),
  };

  // Calculate stats
  const totalTasks = tasks.length;
  const todayTasks = tasks.filter(t => {
    const today = new Date().toISOString().split('T')[0];
    return !!t.dueDate && t.dueDate === today;
  }).length;
  const overdueTasks = tasks.filter(t => {
    const today = new Date().toISOString().split('T')[0];
    return !!t.dueDate && t.dueDate < today && t.status !== 'done';
  }).length;
  const blockedTasks = tasks.filter(t => t.status === 'blocked').length;
  const completionRate = tasks.length > 0 
    ? Math.round((tasks.filter(t => t.status === 'done').length / tasks.length) * 100) 
    : 0;

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    const newStatus = destination.droppableId;
    const updatedTasks = tasks.map(task =>
      task.id === draggableId ? { ...task, status: newStatus } : task
    );

    // Optimistically update UI
    setTasks(updatedTasks);
    
    try {
      // Persist to backend
      await taskService.updateTask(draggableId, { status: newStatus });
      toast.success(`Task moved to ${newStatus.replace('-', ' ')}`);
    } catch (error) {
      console.error('Failed to update task status:', error);
      toast.error('Failed to update task status');
      // Revert on error
      await loadTasks();
    }
  };

  const handleAddTask = async (newTask) => {
    try {
      const createdTask = await taskService.createTask(weddingId, activeOrg.id, newTask);
      
      // Resolve team member name if assigned
      if (createdTask.assignedTo && teamMembers.length > 0) {
        const member = teamMembers.find(m => m.id === createdTask.assignedTo);
        if (member) {
          createdTask.assignedToName = member.name;
        }
      }
      
      // Update tasks list with the new task (including resolved name)
      setTasks(prev => [...prev, createdTask]);
      toast.success('Task added successfully');
    } catch (error) {
      console.error('Failed to add task:', error);
      toast.error('Failed to add task');
      // Reload all tasks on error
      await loadTasks();
    }
  };

  const handleUpdateTask = async (id, updatedTask) => {
    try {
      const updatedTaskData = await taskService.updateTask(id, updatedTask);
      
      // Resolve team member name if assigned
      if (updatedTaskData.assignedTo && teamMembers.length > 0) {
        const member = teamMembers.find(m => m.id === updatedTaskData.assignedTo);
        if (member) {
          updatedTaskData.assignedToName = member.name;
        }
      }
      
      // Update the task in the list (including resolved name)
      setTasks(prev => prev.map(t => t.id === id ? updatedTaskData : t));
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Failed to update task:', error);
      toast.error('Failed to update task');
      // Reload all tasks on error
      await loadTasks();
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await taskService.deleteTask(id);
      await loadTasks();
      toast.success('Task deleted');
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const columnConfig = {
    todo: {
      title: 'To Do',
      icon: Clock,
      bgColor: 'bg-slate-50',
      borderColor: 'border-slate-300',
    },
    'in-progress': {
      title: 'In Progress',
      icon: Users,
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
    },
    blocked: {
      title: 'Blocked',
      icon: AlertCircle,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
    },
    done: {
      title: 'Done',
      icon: Clock,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
    },
  };

  return (
    <div>
      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <Card className="p-4 border-t-4 border-t-primary">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{totalTasks}</p>
              <p className="text-xs text-muted-foreground">Total Tasks</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-warning">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <Clock className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-bold text-warning">{todayTasks}</p>
              <p className="text-xs text-muted-foreground">Due Today</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-destructive">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10">
              <AlertCircle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <p className="text-2xl font-bold text-destructive">{overdueTasks}</p>
              <p className="text-xs text-muted-foreground">Overdue</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-accent">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-accent/10">
              <AlertCircle className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-2xl font-bold text-accent">{blockedTasks}</p>
              <p className="text-xs text-muted-foreground">Blocked</p>
            </div>
          </div>
        </Card>

        <Card className="p-4 border-t-4 border-t-success">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <Users className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-bold text-success">{completionRate}%</p>
              <p className="text-xs text-muted-foreground">Complete</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-full sm:w-[180px]">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priorities</SelectItem>
            <SelectItem value="high">High Priority</SelectItem>
            <SelectItem value="medium">Medium Priority</SelectItem>
            <SelectItem value="low">Low Priority</SelectItem>
          </SelectContent>
        </Select>

        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <Users className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Filter by assignee" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Assignees</SelectItem>
            {assignees.map((assigneeId) => (
              <SelectItem key={assigneeId} value={assigneeId}>
                {assigneeMap[assigneeId] || assigneeId}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1"></div>

        {canManage && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    onClick={() => setIsDialogOpen(true)}
                    className="gradient-primary flex items-center gap-2"
                    disabled={isBlocked}
                  >
                    <Plus className="h-4 w-4" />
                    Add Task
                  </Button>
                </span>
              </TooltipTrigger>
              {isBlocked && (
                <TooltipContent>
                  <p>🔒 Trial limit reached<br />Upgrade for more tasks</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {Object.entries(columns).map(([status, columnTasks]) => {
            const config = columnConfig[status];
            const Icon = config.icon;

            return (
              <div key={status} className="flex flex-col">
                {/* Column Header */}
                <div className={`p-3 rounded-t-lg ${config.bgColor} border-2 ${config.borderColor} flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4" />
                    <h3 className="font-semibold text-sm">{config.title}</h3>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {columnTasks.length}
                  </Badge>
                </div>

                {/* Droppable Area */}
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 p-2 space-y-3 min-h-[400px] rounded-b-lg border-2 border-t-0 ${config.borderColor} ${
                        snapshot.isDraggingOver ? 'bg-muted/50' : 'bg-background'
                      }`}
                    >
                      {columnTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={snapshot.isDragging ? 'opacity-70' : ''}
                            >
                              <TaskCard
                                task={task}
                                onUpdate={handleUpdateTask}
                                onDelete={handleDeleteTask}
                                canEdit={canManage}
                                canDelete={canManage}
                                isReadOnly={isReadOnly}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}

                      {columnTasks.length === 0 && (
                        <div className="text-center text-muted-foreground text-sm py-8">
                          Drop tasks here
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      {/* Add Task Dialog */}
      <AddTaskDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onAddTask={handleAddTask}
        weddingId={weddingId}
      />

      <BlockedActionDialog
        open={blockedDialogOpen}
        onOpenChange={setBlockedDialogOpen}
        resourceType="tasks"
        limit={taskLimitCheck.limit}
        wedding={wedding}
        onUpgradeSuccess={loadTasks}
      />
    </div>
  );
};

export default TaskModule;
